"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Check, Code, Copy, Sparkles, Zap } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@llmgateway/i18n/react";
import { models, type ModelDefinition } from "@llmgateway/models";
import { getProviderIcon } from "@llmgateway/shared/components";
export type CodingModelsView = "all" | "cheap" | "flagship";
interface RecommendedModel {
  id: string;
  category: "cheap" | "flagship";
}

// Cheap models (mostly Chinese open-weights) are listed first so the default
// "all" view leads with them — we don't want to heavily promote flagship-only
// pricing on a fixed-cost DevPass plan.
const RECOMMENDED_MODELS: RecommendedModel[] = [{
  id: "glm-5.1",
  category: "cheap"
}, {
  id: "kimi-k2.6",
  category: "cheap"
}, {
  id: "qwen3-coder",
  category: "cheap"
}, {
  id: "deepseek-v4-pro",
  category: "cheap"
}, {
  id: "gpt-5.3-codex",
  category: "flagship"
}, {
  id: "claude-opus-4-7",
  category: "flagship"
}, {
  id: "gpt-5.5",
  category: "flagship"
}, {
  id: "gemini-3.1-pro-preview",
  category: "flagship"
}];
interface CodingModelsShowcaseProps {
  uiUrl: string;
  showCTA?: boolean;
  className?: string;
  showTabs?: boolean;
  defaultView?: CodingModelsView;
}
function formatContextSize(size: number): string {
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}K`;
  }
  return size.toString();
}
function formatPrice(price: number): string {
  const perMillion = price * 1e6;
  if (perMillion < 0.01) {
    return perMillion.toFixed(4);
  }
  if (perMillion < 1) {
    return perMillion.toFixed(2);
  }
  return perMillion.toFixed(2);
}
const TAB_DEFINITIONS: {
  value: CodingModelsView;
  labelKey: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  descriptionKey: string;
}[] = [{
  value: "cheap",
  labelKey: "codingModels.cheap",
  icon: Zap,
  descriptionKey: "codingModels.cheapDescription"
}, {
  value: "flagship",
  labelKey: "codingModels.flagship",
  icon: Sparkles,
  descriptionKey: "codingModels.flagshipDescription"
}, {
  value: "all",
  labelKey: "codingModels.all",
  icon: Code,
  descriptionKey: "codingModels.allDescription"
}];
export function CodingModelsShowcase(t0) {
  const $ = _c(66);
  const {
    uiUrl,
    showCTA,
    className,
    showTabs: t1,
    defaultView: t2
  } = t0;
  const showTabs = t1 === undefined ? false : t1;
  const defaultView = t2 === undefined ? "all" : t2;
  const {
    t
  } = useI18n();
  const [copiedModel, setCopiedModel] = useState(null);
  const [view, setView] = useState(defaultView);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map(RECOMMENDED_MODELS.map(_temp));
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const idByCategory = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map(RECOMMENDED_MODELS.map(_temp2));
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const orderIndex = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== className || $[3] !== copiedModel || $[4] !== showTabs || $[5] !== t || $[6] !== view) {
    let t10;
    if ($[12] !== view) {
      t10 = r_1 => view === "all" ? true : r_1.category === view;
      $[12] = view;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const filteredIds = RECOMMENDED_MODELS.filter(t10).map(_temp3);
    let t11;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = (a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0);
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    const recommendedModels = (models as ModelDefinition[]).filter(m => filteredIds.includes(m.id)).sort(t11);
    let t12;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = async modelId => {
        await navigator.clipboard.writeText(modelId);
        setCopiedModel(modelId);
        setTimeout(() => setCopiedModel(null), 2000);
      };
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    const copyToClipboard = t12;
    let t13;
    let t14;
    if ($[16] !== className || $[17] !== showTabs || $[18] !== t || $[19] !== view) {
      let t15;
      if ($[24] !== view) {
        t15 = t_0 => t_0.value === view;
        $[24] = view;
        $[25] = t15;
      } else {
        t15 = $[25];
      }
      const activeTab = TAB_DEFINITIONS.find(t15);
      t7 = `rounded-lg border p-6 ${className ?? ""}`;
      let t16;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <Code className="h-5 w-5" aria-hidden="true" />;
        $[26] = t16;
      } else {
        t16 = $[26];
      }
      let t17;
      if ($[27] !== t) {
        t17 = t("codingModels.showcaseTitle");
        $[27] = t;
        $[28] = t17;
      } else {
        t17 = $[28];
      }
      let t18;
      if ($[29] !== t17) {
        t18 = <div className="flex items-center gap-2">{t16}<p className="font-semibold">{t17}</p></div>;
        $[29] = t17;
        $[30] = t18;
      } else {
        t18 = $[30];
      }
      let t19;
      if ($[31] !== showTabs || $[32] !== t || $[33] !== view) {
        t19 = showTabs && <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/30 p-1" role="tablist" aria-label={t("codingModels.filterAria")}>{TAB_DEFINITIONS.map(tab => {
            const TabIcon = tab.icon;
            const active = view === tab.value;
            return <button key={tab.value} type="button" role="tab" aria-selected={active} onClick={() => setView(tab.value)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}><TabIcon className="h-3.5 w-3.5" />{t(tab.labelKey)}</button>;
          })}</div>;
        $[31] = showTabs;
        $[32] = t;
        $[33] = view;
        $[34] = t19;
      } else {
        t19 = $[34];
      }
      if ($[35] !== t18 || $[36] !== t19) {
        t8 = <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t18}{t19}</div>;
        $[35] = t18;
        $[36] = t19;
        $[37] = t8;
      } else {
        t8 = $[37];
      }
      t13 = "text-sm text-muted-foreground mb-4";
      t14 = showTabs && activeTab ? t(activeTab.descriptionKey) : t("codingModels.defaultDescription");
      $[16] = className;
      $[17] = showTabs;
      $[18] = t;
      $[19] = view;
      $[20] = t13;
      $[21] = t14;
      $[22] = t7;
      $[23] = t8;
    } else {
      t13 = $[20];
      t14 = $[21];
      t7 = $[22];
      t8 = $[23];
    }
    if ($[38] !== t13 || $[39] !== t14) {
      t9 = <p className={t13}>{t14}</p>;
      $[38] = t13;
      $[39] = t14;
      $[40] = t9;
    } else {
      t9 = $[40];
    }
    t5 = "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    let t15;
    if ($[41] !== copiedModel || $[42] !== t || $[43] !== view) {
      t15 = model => {
        const provider = model.providers[0];
        const ProviderIcon = provider ? getProviderIcon(provider.providerId) : null;
        const category = idByCategory.get(model.id);
        return <div key={model.id} className="group relative flex flex-col gap-2 rounded-lg border p-4 transition-all hover:border-primary/50 hover:shadow-sm">{view === "all" && category === "cheap" ? <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400"><Zap className="h-2.5 w-2.5" />{t("codingModels.cheap")}</span> : null}<div className="flex items-start justify-between gap-2"><div className="flex-1 min-w-0"><div className="flex items-center gap-2">{ProviderIcon && <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted"><ProviderIcon className="h-4 w-4" /></div>}<span className="font-medium text-sm truncate">{model.name ?? model.id}</span></div></div></div><div className="flex items-center gap-2 mt-1"><code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code><button type="button" onClick={() => copyToClipboard(model.id)} className="shrink-0 p-1 rounded hover:bg-muted transition-colors" title={t("codingModels.copyModelId")}>{copiedModel === model.id ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 text-muted-foreground" />}</button></div>{provider && <div className="text-xs text-muted-foreground space-y-1 mt-1">{provider.contextSize && <p>{t("codingModels.context")}{" "}<span className="font-mono font-medium text-foreground">{formatContextSize(provider.contextSize)}</span></p>}{(provider.inputPrice !== undefined || provider.outputPrice !== undefined) && <p>{provider.inputPrice !== undefined && <><span className="font-mono font-medium text-foreground">${formatPrice(Number(provider.inputPrice))}</span><span className="text-muted-foreground">{" "}{t("codingModels.input")}</span></>}{provider.outputPrice !== undefined && <><span className="text-muted-foreground mx-1">/</span><span className="font-mono font-medium text-foreground">${formatPrice(Number(provider.outputPrice))}</span><span className="text-muted-foreground">{" "}{t("codingModels.output")}</span></>}<span className="text-muted-foreground/70 text-[10px] ml-1">{t("codingModels.perMillionTokens")}</span></p>}</div>}</div>;
      };
      $[41] = copiedModel;
      $[42] = t;
      $[43] = view;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    t6 = recommendedModels.map(t15);
    $[2] = className;
    $[3] = copiedModel;
    $[4] = showTabs;
    $[5] = t;
    $[6] = view;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[45] !== t5 || $[46] !== t6) {
    t10 = <div className={t5}>{t6}</div>;
    $[45] = t5;
    $[46] = t6;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  const t11 = `${uiUrl}/models?category=code`;
  let t12;
  if ($[48] !== t) {
    t12 = t("codingModels.viewAll");
    $[48] = t;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ArrowRight className="h-3 w-3" />;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t11 || $[52] !== t12) {
    t14 = <a href={t11} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">{t12}{t13}</a>;
    $[51] = t11;
    $[52] = t12;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== showCTA || $[55] !== t) {
    t15 = showCTA && <Button asChild={true}><Link href="/signup">{t("nav.getStarted")}</Link></Button>;
    $[54] = showCTA;
    $[55] = t;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== t14 || $[58] !== t15) {
    t16 = <div className="mt-4 flex items-center justify-between">{t14}{t15}</div>;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t10 || $[61] !== t16 || $[62] !== t7 || $[63] !== t8 || $[64] !== t9) {
    t17 = <div className={t7}>{t8}{t9}{t10}{t16}</div>;
    $[60] = t10;
    $[61] = t16;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  return t17;
}
function _temp3(r_2) {
  return r_2.id;
}
function _temp2(r_0, i) {
  return [r_0.id, i] as const;
}
function _temp(r) {
  return [r.id, r.category] as const;
}